import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router";

import { Button, Form, Input, message, Select, Spin, Switch } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { RxLayers } from "react-icons/rx";
import CustomBreadcrumb from "../../../components/breadcrumb/Breadcrumb";

import {
  createArticleGroup,
  getArticlesFiltered,
  getBranchOfficesFiltered,
  getBrandsFiltered,
  getCategoriesFiltered,
} from "../../../api/Services";

import styles from "./NewArticleGroup.module.scss";

const NewArticleGroup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const getBasePath = () => {
    if (location.pathname.startsWith('/commercial-intelligence')) {
      return '/commercial-intelligence/configuration/article-groups';
    }
    if (location.pathname.startsWith('/promotions')) {
      return '/promotions/articlegroups';
    }
    return '/pricings/articlegroups';
  };

  const [loading, setLoading] = useState(false);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [articleOptions, setArticleOptions] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [branchOffices, setBranchOffices] = useState<any[]>([]);

  const [form] = Form.useForm();

  useEffect(() => {
    handleSearchArticles("");
    handleSearchBrands("");
    handleSearchCategories("");
  }, []);

  const handleSearchCategories = async (value: string) => {
    const response = await getCategoriesFiltered(value);
    setCategories(response);
  };

  const getOptionsCategories = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      const parentsString = item?.parents
        ? item.parents.filter(Boolean).join(" - ")
        : "";
      const categoryName = item?.name || "";

      const categoryTree = parentsString
        ? `${parentsString} - ${categoryName}`
        : categoryName;

      options.push(
        <Select.Option value={item.id} key={item.id}>
          {categoryTree}
        </Select.Option>
      );
    });

    return options;
  };

  const handleSearchBrands = async (value: string) => {
    const response = await getBrandsFiltered(value);
    setBrands(response);
  };

  const handleSearchBranchOffices = async (value: string) => {
    const response = await getBranchOfficesFiltered(0, {
      name: value,
    });
    setBranchOffices(response.branchOffices);
  };
  const getOptions = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.name}
        </Select.Option>
      );
    });

    return options;
  };

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const {
        name,
        description,
        state,
        categoryId,
        brandId,
        branchOfficeId,
        articleIds,
      } = values;
      const response = await createArticleGroup({
        name,
        description,
        state,
        categoryId,
        brandId,
        branchOfficeId,
        articleIds,
      });
      if (response) {
        message.success(t("Common-CreateSuccessMessage"));
        form.resetFields();
        navigate(getBasePath());
      } else message.error(t("Common-ErrorMessage"));

      setLoading(false);
    } catch (error) {
      message.error(t("Common-ValidationErrorMessage"));
    }
  };

  const handleSearchArticles = async (value: string) => {
    setLoadingArticles(true);
    setArticleOptions([]);
    const response = await getArticlesFiltered(0, { articleName: value });

    const options = response.articles.map((item: any) => ({
      value: item.id,
      label: item.name,
    }));
    setArticleOptions(options);
    setLoadingArticles(false);
  };

  return loading ? (
    <Spin
      indicator={<LoadingOutlined className="ol-loading-spin-icon" />}
      delay={500}
    />
  ) : (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <CustomBreadcrumb
            items={[
              {
                icon: <RxLayers />,
                label: t("Common-ArticleGroups"),
                path: getBasePath()
              },
              {
                label: t("Common-NewArticleGroup"),
                isCurrentPage: true
              }
            ]}
          />
        </div>
        <div className={styles.actionButtonsWrapper}>
          <Button
            onClick={handleCreate}
            className={styles.saveButton}
            type="primary"
          >
            {t("Common-Add")}
          </Button>
        </div>
      </div>
      <div className={styles.contentWrapper}>
        <div className={styles.articleGroupInfoWrapper}>
          <Form
            name="articleGroup"
            layout="vertical"
            initialValues={{ remember: true }}
            autoComplete="off"
            form={form}
            className={styles.newForm}
          >
            <div className={styles.sectionTitle}>{t("Common-BasicInformation")}</div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Name")}
                name="name"
                rules={[
                  {
                    required: true,
                    message: t("Common-Name"),
                  },
                ]}
              >
                <Input placeholder={t("Common-Name")} />
              </Form.Item>
              <Form.Item
                className={styles.formItem}
                rules={[
                  {
                    required: true,
                    message: t("Common-Description"),
                  },
                ]}
                label={t("Common-Description")}
                name="description"
              >
                <Input placeholder={t("Common-Description")} />
              </Form.Item>
            </div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                initialValue={false}
                label={t("Common-Active")}
                name="state"
                rules={[
                  {
                    required: true,
                    message: t("Common-Active"),
                  },
                ]}
              >
                <Switch />
              </Form.Item>
              <div className={styles.formItem}></div>
            </div>
            <div className={styles.sectionTitle}>{t("Common-Classification")}</div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Category")}
                name="categoryId"
              >
                <Select
                  showSearch
                  allowClear
                  style={{ width: "100%" }}
                  onSearch={handleSearchCategories}
                  placeholder={t("Common-SearchByName")}
                  filterOption={(input: string, option: any) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {getOptionsCategories(categories)}
                </Select>
              </Form.Item>
              <Form.Item 
                className={styles.formItem}
                label={t("Common-Brand")} 
                name="brandId"
              >
                <Select
                  allowClear
                  showSearch
                  filterOption={(input: string, option: any) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: "100%" }}
                  placeholder={t("Common-SearchByName")}
                  onSearch={handleSearchBrands}
                >
                  {getOptions(brands)}
                </Select>
              </Form.Item>
            </div>
            <div className={styles.itemWrapper}>
              <Form.Item 
                className={styles.formItem}
                label={t("Common-BranchOffice")} 
                name="branchOfficeId"
              >
                <Select
                  allowClear
                  showSearch
                  filterOption={(input: string, option: any) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: "100%" }}
                  placeholder={t("Common-SearchByName")}
                  onSearch={handleSearchBranchOffices}
                >
                  {getOptions(branchOffices)}
                </Select>
              </Form.Item>
              <div className={styles.formItem}></div>
            </div>
            
            <div className={styles.sectionTitle}>{t("Common-Articles")}</div>
            <div className={styles.itemWrapper}>
              <Form.Item 
                className={styles.formItem}
                label={t("Common-Articles")} 
                name="articleIds"
              >
                <Select
                  mode="multiple"
                  showSearch
                  allowClear
                  placeholder={t("Common-SearchArticleTextPlaceholder")}
                  loading={loadingArticles}
                  filterOption={(input: string, option: any) =>
                    option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: "100%" }}
                  onSearch={handleSearchArticles}
                  options={articleOptions}
                  className={styles.articlesSelect}
                  maxTagCount="responsive"
                />
              </Form.Item>
              <div className={styles.formItem}></div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};
export default NewArticleGroup;
